import {
  clientAuthorizedGet,
  clientAuthorizedPost,
  clientAuthorizedPatch,
  clientAuthorizedDelete,
} from 'utils/fetchUtils';
import { API_ROUTES } from 'utils/constants';
import type {
  KanbanColumn,
  KanbanCardData,
  KanbanLabel,
  BoardMember,
  KanbanCardComment,
  KanbanCardCommentAttachment,
  ChecklistItem,
  KanbanCardRecurrence,
} from 'types/kanban';

const BOARDS = API_ROUTES.BOARDS;

/** API list shape (includes position); we map to KanbanColumn (id, title, cardIds) */
export interface ApiList {
  id: string;
  boardId: string;
  title: string;
  position: number;
  cardIds: string[];
  createdAt?: string;
  updatedAt?: string;
}

/** API board shape */
export interface ApiBoard {
  id: string;
  title: string;
  createdAt?: string;
  updatedAt?: string;
  /** Optional background image path set by backend */
  backgroundImagePath?: string | null;
}

/** API archive response */
export interface ApiArchiveResponse {
  archivedCards: Array<{
    id: string;
    archivedAt: string;
    card: KanbanCardData;
    originalListId: string;
    originalListTitle?: string;
    originalPosition: number;
    archivedBy?: string;
  }>;
  archivedLists: Array<{
    id: string;
    archivedAt: string;
    column: KanbanColumn;
    originalPosition: number;
    cardsSnapshot?: Record<string, KanbanCardData>;
    archivedBy?: string;
  }>;
}

/** List boards */
export async function fetchBoards(
  companyId: string | null,
): Promise<ApiBoard[]> {
  if (!companyId) return [];
  return clientAuthorizedGet<ApiBoard[]>(BOARDS, undefined, companyId);
}

/** Get one board */
export async function fetchBoard(
  boardId: string,
  companyId: string | null,
): Promise<ApiBoard | null> {
  if (!companyId) return null;
  return clientAuthorizedGet<ApiBoard>(
    `${BOARDS}/${boardId}`,
    undefined,
    companyId,
  );
}

/** Create board */
export async function createBoard(
  title: string,
  companyId: string | null,
): Promise<ApiBoard> {
  return clientAuthorizedPost<ApiBoard>(
    BOARDS,
    { title },
    companyId ?? undefined,
  );
}

/** Update board */
export async function updateBoard(
  boardId: string,
  data: { title?: string },
  companyId: string | null,
): Promise<ApiBoard> {
  return clientAuthorizedPatch<ApiBoard>(
    `${BOARDS}/${boardId}`,
    data,
    companyId ?? undefined,
  );
}

/** Delete board */
export async function deleteBoard(
  boardId: string,
  companyId: string | null,
): Promise<void> {
  await clientAuthorizedDelete(
    `${BOARDS}/${boardId}`,
    undefined,
    companyId ?? undefined,
  );
}

/** List lists (columns) for a board */
export async function fetchLists(
  boardId: string,
  companyId: string | null,
): Promise<ApiList[]> {
  if (!companyId) return [];
  const lists = await clientAuthorizedGet<ApiList[]>(
    `${BOARDS}/${boardId}/lists`,
    undefined,
    companyId,
  );
  return lists ?? [];
}

/** Create list */
export async function createList(
  boardId: string,
  data: { title: string; position?: number },
  companyId: string | null,
): Promise<ApiList> {
  return clientAuthorizedPost<ApiList>(
    `${BOARDS}/${boardId}/lists`,
    data,
    companyId ?? undefined,
  );
}

/** Update list */
export async function updateList(
  boardId: string,
  listId: string,
  data: { title?: string; position?: number },
  companyId: string | null,
): Promise<ApiList> {
  return clientAuthorizedPatch<ApiList>(
    `${BOARDS}/${boardId}/lists/${listId}`,
    data,
    companyId ?? undefined,
  );
}

/** Reorder lists */
export async function reorderLists(
  boardId: string,
  listIds: string[],
  companyId: string | null,
): Promise<ApiList[]> {
  if (!companyId) return [];
  return clientAuthorizedPatch<ApiList[]>(
    `${BOARDS}/${boardId}/lists/reorder`,
    { listIds },
    companyId,
  );
}

/** Copy list */
export async function copyList(
  boardId: string,
  listId: string,
  data: { title?: string },
  companyId: string | null,
): Promise<ApiList> {
  return clientAuthorizedPost<ApiList>(
    `${BOARDS}/${boardId}/lists/${listId}/copy`,
    data,
    companyId ?? undefined,
  );
}

/** Archive list */
export async function archiveList(
  boardId: string,
  listId: string,
  companyId: string | null,
): Promise<{ lists: ApiList[] }> {
  return clientAuthorizedPost<{ lists: ApiList[] }>(
    `${BOARDS}/${boardId}/lists/${listId}/archive`,
    undefined,
    companyId ?? undefined,
  );
}

/** Delete list (if API supports; otherwise use archive) */
export async function deleteList(
  boardId: string,
  listId: string,
  companyId: string | null,
): Promise<void> {
  await clientAuthorizedDelete(
    `${BOARDS}/${boardId}/lists/${listId}`,
    undefined,
    companyId ?? undefined,
  );
}

/** List all cards on board */
export async function fetchCards(
  boardId: string,
  companyId: string | null,
): Promise<KanbanCardData[]> {
  if (!companyId) return [];
  const cards = await clientAuthorizedGet<KanbanCardData[]>(
    `${BOARDS}/${boardId}/cards`,
    undefined,
    companyId,
  );
  return cards ?? [];
}

/** Create card */
export async function createCard(
  boardId: string,
  listId: string,
  data: {
    title: string;
    description?: string;
    position?: number;
    labelIds?: string[];
    memberIds?: string[];
    checklist?: ChecklistItem[];
    startAt?: string;
    dueAt?: string;
    reminderLeadValue?: number;
    reminderLeadUnit?: 'days' | 'hours' | 'minutes' | 'seconds';
  },
  companyId: string | null,
): Promise<KanbanCardData> {
  return clientAuthorizedPost<KanbanCardData>(
    `${BOARDS}/${boardId}/lists/${listId}/cards`,
    data,
    companyId ?? undefined,
  );
}

/**
 * Comment payload for PATCH: API does not accept `attachments`.
 * Board background and profile picture store only a path and use a separate
 * download-URL endpoint for display. Comment image content lives in
 * comment.text (e.g. markdown with URLs); we must not resend attachment
 * metadata when updating the card.
 */
function commentsForPatch(comments: KanbanCardComment[]): Array<{
  id: string;
  text: string;
  authorName: string;
  createdAt: string;
  authorId?: string;
}> {
  return comments.map((c) => {
    const { id, text, authorName, createdAt, authorId } = c;
    return authorId != null
      ? { id, text, authorName, createdAt, authorId }
      : { id, text, authorName, createdAt };
  });
}

/** Update card */
export async function updateCard(
  boardId: string,
  cardId: string,
  data: Partial<{
    title: string;
    description: string;
    listId: string;
    targetBoardId: string;
    position: number;
    labelIds: string[];
    memberIds: string[];
    checklist: ChecklistItem[];
    comments: KanbanCardComment[];
    startAt: string | null;
    dueAt: string | null;
    reminderLeadValue: number;
    reminderLeadUnit: 'days' | 'hours' | 'minutes' | 'seconds';
    recurrence: KanbanCardRecurrence | null;
    advanceDueToNextRecurrence: boolean;
  }>,
  companyId: string | null,
): Promise<KanbanCardData> {
  const payload: Record<string, unknown> = { ...data };
  if (payload.comments != null && Array.isArray(payload.comments)) {
    payload.comments = commentsForPatch(
      payload.comments as KanbanCardComment[],
    );
  }
  return clientAuthorizedPatch<KanbanCardData>(
    `${BOARDS}/${boardId}/cards/${cardId}`,
    payload,
    companyId ?? undefined,
  );
}

/** Reorder cards within a list */
export async function reorderCardsInList(
  boardId: string,
  listId: string,
  cardIds: string[],
  companyId: string | null,
): Promise<KanbanCardData[]> {
  if (!companyId) return [];
  return clientAuthorizedPatch<KanbanCardData[]>(
    `${BOARDS}/${boardId}/lists/${listId}/cards/reorder`,
    { cardIds },
    companyId,
  );
}

export interface MoveCardResponse {
  card: KanbanCardData;
  sourceListId: string;
  sourceCardIds: string[];
  targetListId: string;
  targetCardIds: string[];
  operationId: string | null;
}

/** Move card atomically within a board. */
export async function moveCardInBoard(
  boardId: string,
  cardId: string,
  data: { targetListId: string; targetPosition: number; operationId?: string },
  companyId: string | null,
): Promise<MoveCardResponse> {
  if (!companyId) {
    throw new Error('Company ID is required to move card.');
  }
  return clientAuthorizedPost<MoveCardResponse>(
    `${BOARDS}/${boardId}/cards/${cardId}/move`,
    data,
    companyId,
  );
}

/** Add comment to card (optionally with attachment paths from upload + register). */
export async function addCardComment(
  boardId: string,
  cardId: string,
  data: {
    text: string;
    authorName?: string;
    authorId?: string;
    attachments?: KanbanCardCommentAttachment[];
  },
  companyId: string | null,
): Promise<KanbanCardData> {
  return clientAuthorizedPost<KanbanCardData>(
    `${BOARDS}/${boardId}/cards/${cardId}/comments`,
    data as Record<string, unknown>,
    companyId ?? undefined,
  );
}

/** Archive card – returns the archived card record (with id = archive record id) */
export interface ArchivedCardRecord {
  id: string;
  archivedAt: string;
  card: KanbanCardData;
  originalListId: string;
  originalListTitle?: string;
  originalPosition: number;
  archivedBy?: string;
}

export async function archiveCard(
  boardId: string,
  cardId: string,
  companyId: string | null,
): Promise<ArchivedCardRecord> {
  const res = await clientAuthorizedPost<
    ArchivedCardRecord | { archived: ArchivedCardRecord }
  >(
    `${BOARDS}/${boardId}/cards/${cardId}/archive`,
    undefined,
    companyId ?? undefined,
  );
  return 'archived' in res && res.archived
    ? res.archived
    : (res as ArchivedCardRecord);
}

/** Delete card (if API supports) */
export async function deleteCard(
  boardId: string,
  cardId: string,
  companyId: string | null,
): Promise<void> {
  await clientAuthorizedDelete(
    `${BOARDS}/${boardId}/cards/${cardId}`,
    undefined,
    companyId ?? undefined,
  );
}

/** List labels */
export async function fetchLabels(
  boardId: string,
  companyId: string | null,
): Promise<KanbanLabel[]> {
  if (!companyId) return [];
  const labels = await clientAuthorizedGet<KanbanLabel[]>(
    `${BOARDS}/${boardId}/labels`,
    undefined,
    companyId,
  );
  return labels ?? [];
}

/** Create label */
export async function createLabel(
  boardId: string,
  data: { name: string; color?: string },
  companyId: string | null,
): Promise<KanbanLabel> {
  return clientAuthorizedPost<KanbanLabel>(
    `${BOARDS}/${boardId}/labels`,
    data,
    companyId ?? undefined,
  );
}

/** Update label */
export async function updateLabel(
  boardId: string,
  labelId: string,
  data: { name?: string; color?: string },
  companyId: string | null,
): Promise<KanbanLabel> {
  return clientAuthorizedPatch<KanbanLabel>(
    `${BOARDS}/${boardId}/labels/${labelId}`,
    data,
    companyId ?? undefined,
  );
}

/** Delete label */
export async function deleteLabel(
  boardId: string,
  labelId: string,
  companyId: string | null,
): Promise<void> {
  await clientAuthorizedDelete(
    `${BOARDS}/${boardId}/labels/${labelId}`,
    undefined,
    companyId ?? undefined,
  );
}

/** List board members */
export async function fetchMembers(
  boardId: string,
  companyId: string | null,
): Promise<BoardMember[]> {
  if (!companyId) return [];
  const members = await clientAuthorizedGet<BoardMember[]>(
    `${BOARDS}/${boardId}/members`,
    undefined,
    companyId,
  );
  return members ?? [];
}

/** Add board member */
export async function addBoardMember(
  boardId: string,
  data: { userId?: string; name?: string },
  companyId: string | null,
): Promise<BoardMember> {
  return clientAuthorizedPost<BoardMember>(
    `${BOARDS}/${boardId}/members`,
    data,
    companyId ?? undefined,
  );
}

/** Remove board member */
export async function removeBoardMember(
  boardId: string,
  memberId: string,
  companyId: string | null,
): Promise<void> {
  await clientAuthorizedDelete(
    `${BOARDS}/${boardId}/members/${memberId}`,
    undefined,
    companyId ?? undefined,
  );
}

/** Get archive (archived cards + lists) */
export async function fetchArchive(
  boardId: string,
  companyId: string | null,
): Promise<ApiArchiveResponse> {
  if (!companyId) return { archivedCards: [], archivedLists: [] };
  const res = await clientAuthorizedGet<ApiArchiveResponse>(
    `${BOARDS}/${boardId}/archive`,
    undefined,
    companyId,
  );
  return res ?? { archivedCards: [], archivedLists: [] };
}

/** Restore archived card */
export async function restoreArchivedCard(
  boardId: string,
  archivedCardId: string,
  data: { listId?: string; position?: number },
  companyId: string | null,
): Promise<KanbanCardData> {
  return clientAuthorizedPost<KanbanCardData>(
    `${BOARDS}/${boardId}/archive/cards/${archivedCardId}/restore`,
    data,
    companyId ?? undefined,
  );
}

/** Restore archived list */
export async function restoreArchivedList(
  boardId: string,
  archivedListId: string,
  data: { position?: number },
  companyId: string | null,
): Promise<ApiList> {
  return clientAuthorizedPost<ApiList>(
    `${BOARDS}/${boardId}/archive/lists/${archivedListId}/restore`,
    data,
    companyId ?? undefined,
  );
}

/** Permanently delete archived card */
export async function deleteArchivedCard(
  boardId: string,
  archivedCardId: string,
  companyId: string | null,
): Promise<void> {
  await clientAuthorizedDelete(
    `${BOARDS}/${boardId}/archive/cards/${archivedCardId}`,
    undefined,
    companyId ?? undefined,
  );
}

/** Permanently delete archived list */
export async function deleteArchivedList(
  boardId: string,
  archivedListId: string,
  companyId: string | null,
): Promise<void> {
  await clientAuthorizedDelete(
    `${BOARDS}/${boardId}/archive/lists/${archivedListId}`,
    undefined,
    companyId ?? undefined,
  );
}

/** Board background: upload URL response */
export interface BoardBackgroundUploadUrlResponse {
  uploadUrl: string;
  path: string;
}

/** Board background: download URL response */
export interface BoardBackgroundDownloadUrlResponse {
  downloadUrl: string;
}

/** Get upload URL for board background */
export async function getBoardBackgroundUploadUrl(
  boardId: string,
  body: { filename: string; contentType?: string; expiresInSeconds?: number },
  companyId: string | null,
): Promise<BoardBackgroundUploadUrlResponse> {
  if (!companyId) {
    throw new Error('Company ID is required to upload board background.');
  }
  return clientAuthorizedPost<BoardBackgroundUploadUrlResponse>(
    `${BOARDS}/${boardId}/background/upload-url`,
    body as Record<string, unknown>,
    companyId,
  );
}

/** Confirm / save board background path */
export async function patchBoardBackground(
  boardId: string,
  body: { path: string; filename?: string; size?: number; mimeType?: string },
  companyId: string | null,
): Promise<{ backgroundImagePath: string | null }> {
  if (!companyId) {
    throw new Error('Company ID is required to set board background.');
  }
  return clientAuthorizedPatch<{ backgroundImagePath: string | null }>(
    `${BOARDS}/${boardId}/background`,
    body as Record<string, unknown>,
    companyId,
  );
}

/** Get signed download URL for current board background */
export async function getBoardBackgroundDownloadUrl(
  boardId: string,
  companyId: string | null,
): Promise<BoardBackgroundDownloadUrlResponse> {
  if (!companyId) {
    throw new Error('Company ID is required to load board background.');
  }
  return clientAuthorizedGet<BoardBackgroundDownloadUrlResponse>(
    `${BOARDS}/${boardId}/background/download-url`,
    undefined,
    companyId,
  );
}

/** Remove board background image */
export async function deleteBoardBackground(
  boardId: string,
  companyId: string | null,
): Promise<{ removed: boolean }> {
  if (!companyId) {
    throw new Error('Company ID is required to delete board background.');
  }
  const res = await clientAuthorizedDelete<{ removed: boolean } | null>(
    `${BOARDS}/${boardId}/background`,
    undefined,
    companyId,
  );
  return res ?? { removed: true };
}

/** Comment attachment (app default storage): upload URL response */
export interface CommentAttachmentUploadUrlResponse {
  uploadUrl: string;
  path: string;
}

/** Comment attachment: register uploaded file and get download URL */
export interface CommentAttachmentRegisterResponse {
  downloadUrl: string;
}

/** Get upload URL for a comment attachment (uses app default storage) */
export async function getCommentAttachmentUploadUrl(
  boardId: string,
  cardId: string,
  body: {
    filename: string;
    contentType?: string;
    expiresInSeconds?: number;
  },
  companyId: string | null,
): Promise<CommentAttachmentUploadUrlResponse> {
  if (!companyId) {
    throw new Error('Company ID is required to upload comment attachment.');
  }
  return clientAuthorizedPost<CommentAttachmentUploadUrlResponse>(
    `${BOARDS}/${boardId}/cards/${cardId}/comment-attachments/upload-url`,
    body as Record<string, unknown>,
    companyId,
  );
}

/** Register uploaded comment attachment and get download URL */
export async function patchCommentAttachment(
  boardId: string,
  cardId: string,
  body: {
    path: string;
    filename?: string;
    size?: number;
    mimeType?: string;
  },
  companyId: string | null,
): Promise<CommentAttachmentRegisterResponse> {
  if (!companyId) {
    throw new Error('Company ID is required to register comment attachment.');
  }
  return clientAuthorizedPatch<CommentAttachmentRegisterResponse>(
    `${BOARDS}/${boardId}/cards/${cardId}/comment-attachments`,
    body as Record<string, unknown>,
    companyId,
  );
}

/** Get signed download URL for a comment attachment by path (for display). */
export async function getCommentAttachmentDownloadUrl(
  boardId: string,
  cardId: string,
  path: string,
  companyId: string | null,
): Promise<{ downloadUrl: string }> {
  if (!companyId) {
    throw new Error('Company ID is required to get comment attachment URL.');
  }
  return clientAuthorizedGet<{ downloadUrl: string }>(
    `${BOARDS}/${boardId}/cards/${cardId}/comment-attachments/download-url`,
    { path },
    companyId,
  );
}

/** Map API list to frontend column (id, title, cardIds); sort by position */
export function apiListsToColumns(lists: ApiList[]): KanbanColumn[] {
  const sorted = [...lists].sort((a, b) => a.position - b.position);
  return sorted.map((list) => ({
    id: list.id,
    title: list.title,
    cardIds: list.cardIds ?? [],
  }));
}

/**
 * Build columns from lists + cards. Prefer card listId/position when present
 * (so moves survive refresh); otherwise fall back to list.cardIds from the API.
 */
export function buildColumnsFromListsAndCards(
  lists: ApiList[],
  cards: KanbanCardData[],
): KanbanColumn[] {
  const sortedLists = [...lists].sort((a, b) => a.position - b.position);
  return sortedLists.map((list) => {
    const listCards = cards.filter((c) => c.listId === list.id);
    const fromCards =
      listCards.length > 0
        ? [...listCards]
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((c) => c.id)
        : [];
    const cardIds = fromCards.length > 0 ? fromCards : (list.cardIds ?? []);
    return {
      id: list.id,
      title: list.title,
      cardIds,
    };
  });
}

/** Map API cards array to Record<id, KanbanCardData> */
export function apiCardsToRecord(
  cards: KanbanCardData[],
): Record<string, KanbanCardData> {
  const record: Record<string, KanbanCardData> = {};
  cards.forEach((card) => {
    record[card.id] = card;
  });
  return record;
}
